import { type ReactNode, useId } from "react";
import { Dialog, Stack, Typography } from "@mui/material";
import {
  BannerCloseButton,
  BannerCta,
  BannerIconTile,
  BannerImage,
  BannerText,
} from "./BannerParts";
import { BannerCode, BannerCountdown } from "./PromoParts";
import type { BannerVisual } from "./bannerVariants";

/** The offer in large type across the top, with its promo code in the body. */
type PopUpOfferVisual = {
  type: "offer";
  headline: string;
  note?: string;
  code: string;
};

/** The countdown under its label, across the top. */
type PopUpCountdownVisual = {
  type: "countdown";
  label: string;
  seconds: number;
};

export type PopUpVisual =
  | BannerVisual
  | PopUpOfferVisual
  | PopUpCountdownVisual;

type PopUpBannerProps = {
  open: boolean;
  title: string;
  text?: string;
  /** No CTA once the offer is disabled. */
  ctaLabel?: string;
  onCtaClick: () => void;
  onClose: () => void;
  /** Once the closed dialog has faded out. */
  onExited?: () => void;
  visual?: PopUpVisual;
};

/** Centred modal offer. Triggers belong to the caller; the dialog keeps focus and closes on Esc. */
export const PopUpBanner = ({
  open,
  title,
  text,
  ctaLabel,
  onCtaClick,
  onClose,
  onExited,
  visual,
}: PopUpBannerProps) => {
  const titleId = useId();
  const hasHeader = visual?.type === "offer" || visual?.type === "countdown";
  const cta = ctaLabel && (
    <BannerCta
      label={ctaLabel}
      onClick={onCtaClick}
      variant="subtle"
      size="large"
      fullWidth
    />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      data-testid="pop-up-banner"
      slotProps={{
        transition: { onExited },
        paper: {
          sx: (theme) => ({
            position: "relative",
            width: 400,
            maxWidth: `calc(100% - ${theme.spacing(5)})`,
            m: 3,
            borderRadius: 2,
            // The paper takes focus when the dialog opens; it is not a
            // control, so it gets no focus ring of its own.
            "&:focus-visible": { outline: "none" },
          }),
        },
      }}
    >
      {visual?.type === "offer" && (
        <OfferBody visual={visual} titleId={titleId} cta={cta} />
      )}
      {visual?.type === "countdown" && (
        <CountdownBody
          visual={visual}
          title={title}
          text={text}
          titleId={titleId}
          cta={cta}
        />
      )}
      {!hasHeader && (
        <PlainBody
          visual={visual}
          title={title}
          text={text}
          titleId={titleId}
          cta={cta}
        />
      )}
      <BannerCloseButton
        onClose={onClose}
        variant={hasHeader ? "promo" : "subtle"}
        sx={{
          position: "absolute",
          top: (theme) => theme.spacing(3),
          right: (theme) => theme.spacing(3),
        }}
      />
    </Dialog>
  );
};

type BodyProps = {
  titleId: string;
  cta: ReactNode;
};

const PlainBody = ({
  visual,
  title,
  text,
  titleId,
  cta,
}: BodyProps & { visual?: BannerVisual; title: string; text?: string }) => {
  const isImage = visual?.type === "image";

  return (
    <>
      {isImage && (
        <BannerImage
          src={visual.src}
          alt={visual.alt}
          width="100%"
          height={200}
        />
      )}
      <Stack
        spacing={4}
        alignItems={isImage ? "stretch" : "center"}
        px={4}
        pb={4}
        pt={isImage ? 4 : 5.25}
      >
        {visual?.type === "icon" && (
          <BannerIconTile icon={visual.icon} tone="light" size="large" />
        )}
        <BannerText
          title={title}
          text={text}
          variant="subtle"
          size="large"
          align={isImage ? "left" : "center"}
          titleId={titleId}
        />
        {cta}
      </Stack>
    </>
  );
};

const OfferBody = ({
  visual,
  titleId,
  cta,
}: BodyProps & { visual: PopUpOfferVisual }) => (
  <>
    <PromoHeader>
      <Typography
        id={titleId}
        variant="h3"
        fontWeight="bold"
        color="common.white"
      >
        {visual.headline}
      </Typography>
      {visual.note && (
        <Typography variant="body1" color="banner.promoSecondaryText">
          {visual.note}
        </Typography>
      )}
    </PromoHeader>
    <Stack spacing={3} p={4}>
      <Typography variant="body1">Use this code at checkout:</Typography>
      <BannerCode code={visual.code} />
      {cta}
    </Stack>
  </>
);

const CountdownBody = ({
  visual,
  title,
  text,
  titleId,
  cta,
}: BodyProps & {
  visual: PopUpCountdownVisual;
  title: string;
  text?: string;
}) => (
  <>
    <PromoHeader spacing={2}>
      <Typography variant="body2" color="banner.promoSecondaryText">
        {visual.label}
      </Typography>
      <BannerCountdown seconds={visual.seconds} tone="color" look="boxes" />
    </PromoHeader>
    <Stack spacing={3} p={4}>
      <BannerText
        title={title}
        text={text}
        variant="subtle"
        size="large"
        titleId={titleId}
      />
      {cta}
    </Stack>
  </>
);

/** The Promo green band across the top of the Offer and Countdown pop-ups. */
const PromoHeader = ({
  children,
  spacing,
}: {
  children: ReactNode;
  spacing?: number;
}) => (
  <Stack
    alignItems="center"
    textAlign="center"
    spacing={spacing}
    px={4}
    pt={5}
    pb={4.5}
    bgcolor="banner.promoBackground"
  >
    {children}
  </Stack>
);
